import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarDays, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const statusColors = {
  confirmed: 'bg-[#2D6A4F] text-white',
  pending_payment: 'bg-[#D4A373] text-[#1F2A25]',
  completed: 'bg-[#1E3F33] text-white',
  checked_out: 'bg-[#457B9D] text-white',
  pending_inspection: 'bg-[#D4A373] text-[#1F2A25]',
  damage_reported: 'bg-[#BC4749] text-white',
  cancelled: 'bg-[#6B726A] text-white',
};

function ManagerContent() {
  const [bookings, setBookings] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [bRes, uRes] = await Promise.all([
        api.get('/admin/bookings'),
        api.get('/admin/users')
      ]);
      setBookings(bRes.data);
      setCleaners(uRes.data.filter(u => u.role === 'cleaner'));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await api.patch(`/admin/bookings/${bookingId}/status`, { status: newStatus });
      fetchData();
    } catch {}
  };

  const assignCleaner = async (bookingId, cleanerId) => {
    try {
      await api.post(`/admin/bookings/${bookingId}/assign-cleaner`, { cleaner_id: cleanerId });
      fetchData();
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#CC5A3D]" /></div>;

  return (
    <div data-testid="manager-dashboard">
      <h1 className="text-3xl font-heading font-medium text-[#1F2A25] mb-6">Manager Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-[#E6D5B8] p-5 rounded-lg" data-testid="manager-stat-total">
          <p className="text-sm text-[#6B726A]">Total Bookings</p>
          <p className="text-2xl font-heading font-medium text-[#1F2A25]">{bookings.length}</p>
        </div>
        <div className="bg-white border border-[#E6D5B8] p-5 rounded-lg" data-testid="manager-stat-confirmed">
          <p className="text-sm text-[#6B726A]">Confirmed</p>
          <p className="text-2xl font-heading font-medium text-[#2D6A4F]">{bookings.filter(b => b.status === 'confirmed').length}</p>
        </div>
        <div className="bg-white border border-[#E6D5B8] p-5 rounded-lg" data-testid="manager-stat-pending">
          <p className="text-sm text-[#6B726A]">Pending Inspection</p>
          <p className="text-2xl font-heading font-medium text-[#D4A373]">{bookings.filter(b => ['checked_out', 'pending_inspection'].includes(b.status)).length}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E6D5B8] rounded-lg overflow-hidden" data-testid="manager-bookings-table">
        <div className="p-4 border-b border-[#E6D5B8]">
          <h2 className="text-lg font-heading font-medium text-[#1F2A25] flex items-center gap-2"><CalendarDays className="w-5 h-5" /> All Bookings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F0EBE1]">
              <tr>
                <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Guest</th>
                <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Caravan</th>
                <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Dates</th>
                <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Status</th>
                <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Cleaner</th>
                <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b.id} className="border-t border-[#E6D5B8]">
                  <td className="px-4 py-3 font-medium text-[#1F2A25]">{b.guest_name}</td>
                  <td className="px-4 py-3 text-[#6B726A]">{b.caravan_name}</td>
                  <td className="px-4 py-3 text-[#6B726A] whitespace-nowrap">{b.check_in} - {b.check_out}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[b.status] || 'bg-gray-100'}`}>{b.status?.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Select onValueChange={(v) => assignCleaner(b.id, v)} value={b.cleaner_id || ''}>
                      <SelectTrigger className="w-32 h-8 text-xs border-[#E6D5B8]"><SelectValue placeholder="Assign..." /></SelectTrigger>
                      <SelectContent>
                        {cleaners.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {b.status === 'confirmed' && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, 'checked_out')} className="text-xs h-7 border-[#E6D5B8]">Check Out</Button>
                    )}
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6B726A]">No bookings yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function ManagerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || !['owner', 'manager'].includes(user.role))) navigate('/login');
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  return (
    <DashboardLayout>
      <ManagerContent />
    </DashboardLayout>
  );
}
